import type { z } from 'zod';
import { isApiError, errorMessage } from '../api/client';

export interface FormErrorState {
  /** Field path (e.g. "admin.email", "stages.0.name") → first message. */
  fields: Record<string, string>;
  /** Error not tied to a field. */
  general: string | null;
  code?: string | undefined;
  requestId?: string | undefined;
}

export const noErrors: FormErrorState = { fields: {}, general: null };

export const hasErrors = (s: FormErrorState): boolean => s.general !== null || Object.keys(s.fields).length > 0;

export function issuesToFields(issues: { path: string; message: string }[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const i of issues) {
    const key = i.path || '';
    if (!(key in out)) out[key] = i.message;
  }
  return out;
}

/** Replace zod's generic wording for the most common cases; custom schema messages are kept. */
function friendlyMessage(issue: z.core.$ZodIssue): string {
  const i = issue as { code: string; minimum?: number | bigint; maximum?: number | bigint; origin?: string; input?: unknown; message: string; format?: string; expected?: string };
  if (i.code === 'too_small' && i.origin === 'string' && Number(i.minimum) === 1) return 'This field is required';
  if (i.code === 'too_big' && i.origin === 'string') return `Use at most ${String(i.maximum)} characters`;
  if (i.code === 'too_small' && i.origin === 'number') return `Must be at least ${String(i.minimum)}`;
  if (i.code === 'too_big' && i.origin === 'number') return `Must be at most ${String(i.maximum)}`;
  if (i.code === 'invalid_format' && i.format === 'email') return 'Enter a valid email address';
  if (i.code === 'invalid_type' && (i.expected === 'number' || i.expected === 'int')) return 'Enter a whole number';
  if (i.code === 'invalid_type' && i.input === undefined) return 'This field is required';
  return i.message;
}

type ValidateResult<T> = { ok: true; data: T } | { ok: false; errors: FormErrorState };

/** Validate with a shared @oms/contracts schema so client and server rules stay identical. */
export function validate<S extends z.ZodType>(schema: S, data: unknown): ValidateResult<z.output<S>> {
  const res = schema.safeParse(data);
  if (res.success) return { ok: true, data: res.data };
  const fields = issuesToFields(res.error.issues.map((i) => ({ path: i.path.map(String).join('.'), message: friendlyMessage(i) })));
  const general = fields[''] ?? null;
  delete fields[''];
  return { ok: false, errors: { fields, general: general ?? (Object.keys(fields).length ? null : 'Some fields are invalid') } };
}

/** Map any thrown error (usually an ApiError) to form errors; VALIDATION_FAILED details map to fields. */
export function errorsFromApi(e: unknown): FormErrorState {
  if (isApiError(e)) {
    const fields = issuesToFields(e.fieldIssues);
    const rootMsg = fields[''];
    delete fields[''];
    const hasFields = Object.keys(fields).length > 0;
    return {
      fields,
      general: rootMsg ?? (hasFields ? null : errorMessage(e)),
      code: e.code,
      requestId: e.requestId,
    };
  }
  return { fields: {}, general: errorMessage(e) };
}
